import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Scope1Form } from './Scope1Form';
import { Scope2Form } from './Scope2Form';
import { Scope3Form } from './Scope3Form';
import { apiRequest } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import UpgradePrompt from './subscription/UpgradePrompt';

const steps = [
  { id: 1, name: 'Scope 1: Direct Emissions', component: Scope1Form },
  { id: 2, name: 'Scope 2: Indirect Emissions', component: Scope2Form },
  { id: 3, name: 'Scope 3: Other Indirect Emissions', component: Scope3Form },
  { id: 4, name: 'Results' },
];

export function EstimationWizard() {
  const { toast } = useToast();
  const { hasScope3Access } = useSubscription();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [results, setResults] = useState<any[]>([]);

  const mutationOptions = {
    onSuccess: (data: any) => {
      setResults((prev: any[]) => [...prev, data]);
      queryClient.invalidateQueries({ queryKey: ['ghg-results'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  };

  const scope1Mutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/ghg/scope1', data),
    ...mutationOptions
  });
  const scope2Mutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/ghg/scope2', data),
    ...mutationOptions
  });
  const scope3Mutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/ghg/scope3', data),
    ...mutationOptions
  });

  useEffect(() => {
    if (currentStep === 4) {
      if (formData.scope1) scope1Mutation.mutate(formData.scope1);
      if (formData.scope2) scope2Mutation.mutate(formData.scope2);
      if (formData.scope3) scope3Mutation.mutate(formData.scope3);
    }
  }, [currentStep]);

  const handleNext = (data: any) => {
    setFormData((prev: any) => ({ ...prev, ...data }));
    
    // Check if trying to access Scope 3 without subscription
    if (currentStep === 2 && !hasScope3Access) {
      toast({
        title: 'Scope 3 Access Required',
        description: 'Scope 3 emissions tracking requires a subscription upgrade.',
        variant: 'destructive',
      });
      return;
    }
    
    if (currentStep < steps.length) {
      setCurrentStep((prev: number) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev: number) => prev - 1);
    }
  };

  const CurrentFormComponent = steps[currentStep - 1].component;

  const totalEmissions = results.reduce((acc, r) => acc + r.co2_equivalent, 0);

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Environmental Impact Estimation Wizard</CardTitle>
        <div className="mt-4">
          <ul className="flex justify-between">
            {steps.map((step, index) => (
              <li key={step.id} className="text-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index < currentStep ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}>
                  {step.id}
                </div>
                <p className="text-sm mt-2">{step.name}</p>
              </li>
            ))}
          </ul>
        </div>
      </CardHeader>
      <CardContent>
        {CurrentFormComponent && <CurrentFormComponent onSubmit={handleNext} isSubmitting={false} />}
        {currentStep === 3 && !hasScope3Access && (
          <div className="mt-6">
            <UpgradePrompt 
              feature="scope3"
              description="Track Scope 3 emissions from your value chain including business travel, employee commuting, and supply chain activities."
            />
          </div>
        )}
        {currentStep === 4 && (
            <div>
                <h2 className="text-2xl font-bold mb-4">Estimation Results</h2>
                {scope1Mutation.isPending || scope2Mutation.isPending || scope3Mutation.isPending ? (
                  <p>Calculating emissions...</p>
                ) : (
                  <div>
                    <h3 className="text-xl font-semibold">Total Estimated Emissions: {totalEmissions.toFixed(2)} kg CO₂e</h3>
                    <div className="mt-4 space-y-2">
                      {results.map((result, index) => (
                        <div key={index} className="p-4 border rounded-md">
                          <p className="font-bold">{result.scope.toUpperCase()}: {result.activity_type}</p>
                          <p>CO₂ Equivalent: {result.co2_equivalent.toFixed(2)} kg</p>
                          <p className="text-sm text-gray-500">{result.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
        )}
        <div className="flex justify-between mt-8">
          {currentStep > 1 && currentStep < 4 && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
